// Response schema for Home (plan §4 GET /home). Parts a role does not get are absent, so every
// part except the date and the freeze countdown is optional. Unknown keys are dropped.
import { TASK_STATUSES } from '@cis/shared';
import { z } from 'zod';
import { queueItemSchema, SHOWN_TEXT_MAX } from './verification.ts';

const MAX_TEXT = SHOWN_TEXT_MAX;
const MAX_ID = 64;

const homeTaskSchema = z.object({
  taskId: z.string().min(1).max(MAX_ID),
  title: z.string().max(MAX_TEXT),
  phase: z.string().max(MAX_ID),
  area: z.string().max(MAX_TEXT),
  due: z.string().nullable(),
  status: z.enum(TASK_STATUSES),
  percent: z.number().int().min(0).max(100),
  overdue: z.boolean(),
});

const listOf = <T extends z.ZodType>(item: T) =>
  z.object({ items: z.array(item), total: z.number().int().nonnegative() });

export const homeSchema = z.object({
  today: z.string(),
  freeze: z.object({ date: z.string(), daysLeft: z.number().int() }),
  myWeek: listOf(homeTaskSchema).optional(),
  overdue: listOf(homeTaskSchema).optional(),
  sentBack: listOf(
    z.object({
      taskId: z.string().min(1).max(MAX_ID),
      title: z.string().max(MAX_TEXT),
      reason: z.string().max(MAX_TEXT).nullable(),
      by: z.string().max(MAX_TEXT),
      at: z.string(),
    }),
  ).optional(),
  myQueue: listOf(queueItemSchema).optional(),
  phases: z
    .array(
      z.object({
        code: z.string().max(MAX_ID),
        title: z.string().max(MAX_TEXT),
        total: z.number().int().nonnegative(),
        done: z.number().int().nonnegative(),
        verified: z.number().int().nonnegative(),
        percent: z.number().int().min(0).max(100),
      }),
    )
    .optional(),
  program: z
    .object({
      evidence: z.record(z.string(), z.number().int().nonnegative()),
      overdueByArea: z.record(z.string(), z.number().int().nonnegative()),
    })
    .optional(),
  leads: z
    .object({
      openRisks: z.number().int().nonnegative(),
      pendingAsks: z.number().int().nonnegative(),
      acceptancesToSign: z.number().int().nonnegative(),
    })
    .optional(),
  backup: z
    .object({ ageHours: z.number().int().nonnegative().nullable(), stale: z.boolean() })
    .optional(),
});

export type Home = z.infer<typeof homeSchema>;
export type HomeTask = z.infer<typeof homeTaskSchema>;
